/**
 * VibeCheck CLI v2 — File Walker
 * Fast file discovery with glob patterns and smart defaults.
 */

import * as fs from 'fs';
import * as path from 'path';
import fg from 'fast-glob';

const DEFAULT_INCLUDE = [
  'src/**/*.{ts,tsx,js,jsx,py,go,rs,java,rb}',
  'app/**/*.{ts,tsx,js,jsx}',
  'pages/**/*.{ts,tsx,js,jsx}',
  'lib/**/*.{ts,tsx,js,jsx}',
  'components/**/*.{ts,tsx,js,jsx}',
  'api/**/*.{ts,tsx,js,jsx}',
  'server/**/*.{ts,tsx,js,jsx}',
  'routes/**/*.{ts,tsx,js,jsx}',
  'middleware/**/*.{ts,tsx,js,jsx}',
  'utils/**/*.{ts,tsx,js,jsx}',
  'hooks/**/*.{ts,tsx,js,jsx}',
  'services/**/*.{ts,tsx,js,jsx}',
];

const DEFAULT_EXCLUDE = [
  '**/node_modules/**',
  '**/dist/**',
  '**/build/**',
  '**/.next/**',
  '**/.nuxt/**',
  '**/coverage/**',
  '**/.git/**',
  '**/*.min.js',
  '**/*.map',
  '**/*.d.ts',
  '**/vendor/**',
  '**/__pycache__/**',
];

export interface WalkOptions {
  include?: string[];
  exclude?: string[];
  maxFiles?: number;
}

export async function walkFiles(rootPath: string, options: WalkOptions = {}): Promise<string[]> {
  const include = options.include?.length ? options.include : DEFAULT_INCLUDE;
  const exclude = options.exclude?.length ? options.exclude : DEFAULT_EXCLUDE;

  const files = await fg(include, {
    cwd: rootPath,
    ignore: exclude,
    absolute: false,
    dot: false,
    onlyFiles: true,
  });

  if (options.maxFiles && files.length > options.maxFiles) {
    return files.slice(0, options.maxFiles);
  }

  return files;
}

export async function walkSingleFile(rootPath: string, filePath: string): Promise<string | null> {
  const abs = path.resolve(rootPath, filePath);
  if (fs.existsSync(abs)) return filePath;
  return null;
}

export async function walkDir(rootPath: string, dirPath: string, options: WalkOptions = {}): Promise<string[]> {
  const relDir = path.relative(rootPath, path.resolve(rootPath, dirPath));
  const include = [`${relDir}/**/*.{ts,tsx,js,jsx,py,go,rs}`];
  return walkFiles(rootPath, { ...options, include });
}

const EXCLUDED_DIR_PREFIXES = ['node_modules/', 'dist/', 'build/', '.next/', '.nuxt/', 'coverage/', 'vendor/', '__pycache__/'];

function filterExcludedPaths(files: string[]): string[] {
  return files.filter(f => {
    const normalized = f.replace(/\\/g, '/');
    return !EXCLUDED_DIR_PREFIXES.some(prefix => normalized.startsWith(prefix) || normalized.includes('/' + prefix));
  });
}

export async function walkStagedFiles(rootPath: string): Promise<string[]> {
  try {
    const { execSync } = await import('child_process');
    const output = execSync('git diff --cached --name-only --diff-filter=ACM', {
      cwd: rootPath,
      encoding: 'utf-8',
    });
    return filterExcludedPaths(output.trim().split('\n').filter(f => f.length > 0));
  } catch {
    return [];
  }
}

export async function walkChangedFiles(rootPath: string): Promise<string[]> {
  try {
    const { execSync } = await import('child_process');
    const output = execSync('git diff --name-only HEAD', {
      cwd: rootPath,
      encoding: 'utf-8',
    });
    return filterExcludedPaths(output.trim().split('\n').filter(f => f.length > 0));
  } catch {
    return [];
  }
}

export function readFileContent(rootPath: string, filePath: string): string | null {
  try {
    const abs = path.resolve(rootPath, filePath);
    return fs.readFileSync(abs, 'utf-8');
  } catch {
    return null;
  }
}
